import {
  getIngredientStock,
  isBufferIngredient,
  isNullOrUndefined,
  Round,
} from 'utils/Global';
import { BufferCalculator } from 'business/BufferCalculator';
import { TargetLayer } from 'business/models/CustomModels';
import { DrawingLayer } from 'business/models/DrawingLayer';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  Ingredient,
  IngredientIngredientType,
  IngredientStockDTO,
  LayerDTO,
} from 'business/models/Models';
import {
  getExpWells,
  getIngIngType,
  getIngredient,
  getIngredientType,
} from './GenerateCommon';
import { VolumetricWellLayer } from './VolumetricWellLayer';
import { VolumetricWellLayers } from './VolumetricWellLayers';
import { VolumetricWellDropLayers } from './VolumetricWellDropLayers';
import { VolumetricWellDropLayer } from './VolumetricWellDropLayer';
import { Drop, WellIng } from 'components/Experiment/constants';

export function Compute(
  targetLayer: TargetLayer,
  layer: DrawingLayer,
  docData: ExperimentDtoClient,
) {
  let ingredientVolumeSum = 0;
  const targetLayerVolume: number = targetLayer.volume;
  // Calculate ingredient volumes using the pre-mixed concentrations
  for (const targetLayerIngredient of targetLayer.targetLayerIngredientList) {
    const concentration: number = targetLayerIngredient.preMixedConcentration;
    const stockConcentration: number =
      targetLayerIngredient.ingredientStock?.stockConcentration;

    // Calculate volume for the ingredient - wellIngredient
    let volume = 0;
    // volume should be 0 if conc is 0 ( we allow stock conc to be 0 resulting volume = NaN )
    if (concentration != 0) {
      if (concentration > stockConcentration) {
        // volume should be 0 if conc is 0 ( we allow stock conc to be 0 and it causes
        setLayerError(
          'The given concentration is higher than the stock concentration.',
          targetLayer,
        );
      }
    }
    volume = targetLayerVolume * (concentration / stockConcentration);

    targetLayerIngredient.volume = volume;
    ingredientVolumeSum += volume;

    const targetLayerIngredientVolume: number = targetLayerIngredient.volume;

    const ingredientIngredientTypeRow: IngredientIngredientType = getIngIngType(
      targetLayerIngredient.ingredientIngredientTypeId,
    );

    const ingType = getIngredientType(ingredientIngredientTypeRow.ingredientTypeId);
    if (ingType.isBuffer) {
      if (
        (targetLayerIngredient.pH < targetLayerIngredient.ingredientStock?.stockPh ||
          targetLayerIngredient.pH > targetLayerIngredient.highPhIngStock?.stockPh) &&
        !targetLayer.hasError
      ) {
        setLayerError(
          `'PH' must be greater than or equal to ${targetLayerIngredient.ingredientStock?.stockPh} 
          and less than or equal to ${targetLayerIngredient.highPhIngStock?.stockPh}`,
          targetLayer,
        );
      }

      if (targetLayerIngredient.highPhIngStock == null) {
        // Single vial buffer.
        if (
          targetLayerIngredient.lowPHVolume == null ||
          targetLayerIngredient.lowPHVolume != targetLayerIngredientVolume
        ) {
          targetLayerIngredient.lowPHVolume = targetLayerIngredientVolume;
        }

        if (targetLayerIngredient.highPhIngStock != null) {
          targetLayerIngredient.highPhIngStock = null;
        }
      } else {
        // Dual vial buffer. Calculate the correct Buffer Calculation for Low Fraction
        const lowVialStock: IngredientStockDTO = targetLayerIngredient.ingredientStock;
        const highVialStock: IngredientStockDTO = targetLayerIngredient.highPhIngStock;

        const ingredient: Ingredient = getIngredient(lowVialStock.ingredientId);
        const bc: BufferCalculator = new BufferCalculator(
          ingredient,
          lowVialStock,
          highVialStock,
        );

        const lowFraction: number = bc.LowFraction(targetLayerIngredient.pH);
        const lowpHVolume: number = Round(lowFraction * targetLayerIngredientVolume);
        const highpHVolume: number = Round(targetLayerIngredientVolume - lowpHVolume);

        if (
          targetLayerIngredient.lowPHVolume == null ||
          targetLayerIngredient.lowPHVolume != lowpHVolume
        ) {
          targetLayerIngredient.lowPHVolume = lowpHVolume;
        }

        if (
          targetLayerIngredient.highPHVolume == null ||
          targetLayerIngredient.highPHVolume != highpHVolume
        ) {
          targetLayerIngredient.highPHVolume = highpHVolume;
        }
      }
    } else {
      // Non-buffer.
      if (targetLayerIngredient.pH != null) {
        targetLayerIngredient.pH = null;
      }

      if (targetLayerIngredient.lowPHVolume != null) {
        targetLayerIngredient.lowPHVolume = null;
      }

      if (targetLayerIngredient.highPHVolume != null) {
        targetLayerIngredient.highPHVolume = null;
      }
    }
  }

  // Calculate water volumes
  if (targetLayerVolume < ingredientVolumeSum) {
    setLayerError(
      'The ingredients volume in the layer exceeds the layer volume.',
      targetLayer,
    );
    targetLayer.waterVolume = 0;
  } else {
    targetLayer.waterVolume = targetLayerVolume - ingredientVolumeSum; // Round(targetLayerVolume - ingredientVolumeSum);
  }
  //ToDo: need to remove it from here
}

export function computeVolume(target, targetLayer, layer) {
  let ingredientVolumeSum = 0;
  let targetlayerVolume;
  let targetLayerIngredientList;

  if (layer.targetDrop < 0) {
    targetlayerVolume = targetLayer.volume;
    targetLayerIngredientList = targetLayer.wellLayerIngredientList;
  } else {
    targetlayerVolume = targetLayer.volume;
    targetLayerIngredientList = targetLayer.dropLayerIngredientList;
  }

  if (targetLayerIngredientList == undefined) {
    return;
  }

  for (let i = 0; i < targetLayerIngredientList.length; i++) {
    const targetLayerIngredient = targetLayerIngredientList[i];
    const concentration = targetLayerIngredient.preMixedConcentration;
    const stockConcentration = targetLayerIngredient.stockConcentration;

    //calculate volume for the ingredient - wellIngredient
    let volume = 0;
    // volume should be 0 if conc is 0 ( we allow stock conc to be 0 resulting volume = NaN )
    if (concentration !== 0) {
      // volume should be 0 if conc is 0 ( we allow stock conc to be 0 and it causes
      volume = Round(targetlayerVolume * (concentration / stockConcentration));
    }

    targetLayerIngredient.volume = volume;
    ingredientVolumeSum += volume;

    const targetLayerIngredientVolume = targetLayerIngredient.volume;

    if (targetLayerIngredient.isBuffer) {
      if (targetLayerIngredient.highPhIngStockId === null) {
        // Single vial buffer.
        if (
          targetLayerIngredient.lowPHVolume === null ||
          targetLayerIngredient.lowPHVolume != targetLayerIngredientVolume
        ) {
          targetLayerIngredient.lowPHVolume = targetLayerIngredientVolume;
        }

        if (targetLayerIngredient.highPHVolume !== null) {
          targetLayerIngredient.highPHVolume = null;
        }
      } else {
        // TODO .. for h2
        // Dual vial buffer. Calculate the correct Buffer Calculation for Low Fraction
        const lowVialStock = targetLayerIngredient.ingredientStock;
        const highVialStock = targetLayerIngredient.highPhIngStock;
        const ingredient = targetLayerIngredient.ingredientStockIngredient;
        const bc = new BufferCalculator(ingredient, lowVialStock, highVialStock);
        const lowFraction = bc.LowFraction(targetLayerIngredient.pH);
        const lowpHVolume = Round(lowFraction * targetLayerIngredientVolume);
        const highpHVolume = Round(targetLayerIngredientVolume - lowpHVolume);
        if (
          targetLayerIngredient.lowPHVolume == null ||
          targetLayerIngredient.lowPHVolume !== lowpHVolume
        ) {
          targetLayerIngredient.lowPHVolume = lowpHVolume;
        }
        if (
          targetLayerIngredient.highPHVolume == null ||
          targetLayerIngredient.highPHVolume !== highpHVolume
        ) {
          targetLayerIngredient.highPHVolume = highpHVolume;
        }
      }
    } else {
      // Non-buffer.
      if (targetLayerIngredient.pH !== null) {
        targetLayerIngredient.pH = null;
      }
      if (targetLayerIngredient.lowPHVolume !== null) {
        targetLayerIngredient.lowPHVolume = null;
      }
      if (targetLayerIngredient.highPHVolume !== null) {
        targetLayerIngredient.highPHVolume = null;
      }
    }
  }

  // Calculate water volumes
  if (layer.targetDrop < 0) {
    targetLayer.waterVolume = Round(layer.volume - ingredientVolumeSum);

    targetLayer.volume = layer.volume;

    target.wellWaterVolume += targetLayer.waterVolume;
  } else {
    targetLayer.waterVolume = Round(layer.volume - ingredientVolumeSum);

    targetLayer.volume = layer.volume;

    target.waterVolume += targetLayer.waterVolume;
  }
}

export function Compute1(
  targetLayer: TargetLayer,
  layer: LayerDTO,
  docData: ExperimentDtoClient,
) {
  let ingredientVolumeSum = 0;
  const targetLayerVolume: number = targetLayer.volume;
  // Calculate ingredient volumes using the pre-mixed concentrations
  for (const targetLayerIngredient of targetLayer.targetLayerIngredientList) {
    const concentration: number = targetLayerIngredient.preMixedConcentration;
    const stockConcentration: number =
      targetLayerIngredient.ingredientStock.stockConcentration;

    // Calculate volume for the ingredient - wellIngredient
    let volume = 0;
    // volume should be 0 if conc is 0 ( we allow stock conc to be 0 resulting volume = NaN )
    if (concentration != 0) {
      // volume should be 0 if conc is 0 ( we allow stock conc to be 0 and it causes
      volume = Round(targetLayerVolume * (concentration / stockConcentration));
    }

    targetLayerIngredient.volume = volume;
    ingredientVolumeSum += volume;

    const targetLayerIngredientVolume: number = targetLayerIngredient.volume;

    const ingredientIngredientTypeRow: IngredientIngredientType = getIngIngType(
      targetLayerIngredient.ingredientIngredientTypeId,
    );

    const ingType = getIngredientType(ingredientIngredientTypeRow.ingredientTypeId);
    if (ingType.isBuffer) {
      if (targetLayerIngredient.highPhIngStock == null) {
        // Single vial buffer.
        if (
          targetLayerIngredient.lowPHVolume == null ||
          targetLayerIngredient.lowPHVolume != targetLayerIngredientVolume
        ) {
          targetLayerIngredient.lowPHVolume = targetLayerIngredientVolume;
        }

        if (targetLayerIngredient.highPhIngStock != null) {
          targetLayerIngredient.highPhIngStock = null;
        }
      } else {
        // Dual vial buffer. Calculate the correct Buffer Calculation for Low Fraction
        const lowVialStock: IngredientStockDTO = targetLayerIngredient.ingredientStock;
        const highVialStock: IngredientStockDTO = targetLayerIngredient.highPhIngStock;

        const ingredient: Ingredient = getIngredient(lowVialStock.ingredientId);
        const bc: BufferCalculator = new BufferCalculator(
          ingredient,
          lowVialStock,
          highVialStock,
        );

        const lowFraction: number = bc.LowFraction(targetLayerIngredient.pH);
        const lowpHVolume: number = Round(lowFraction * targetLayerIngredientVolume);
        const highpHVolume: number = Round(targetLayerIngredientVolume - lowpHVolume);

        if (
          targetLayerIngredient.lowPHVolume == null ||
          targetLayerIngredient.lowPHVolume != lowpHVolume
        ) {
          targetLayerIngredient.lowPHVolume = lowpHVolume;
        }

        if (
          targetLayerIngredient.highPHVolume == null ||
          targetLayerIngredient.highPHVolume != highpHVolume
        ) {
          targetLayerIngredient.highPHVolume = highpHVolume;
        }
      }
    } else {
      // Non-buffer.
      if (targetLayerIngredient.pH != null) {
        targetLayerIngredient.pH = null;
      }

      if (targetLayerIngredient.lowPHVolume != null) {
        targetLayerIngredient.lowPHVolume = null;
      }

      if (targetLayerIngredient.highPHVolume != null) {
        targetLayerIngredient.highPHVolume = null;
      }
    }
  }

  // Calculate water volumes
  targetLayer.waterVolume = Round(targetLayerVolume - ingredientVolumeSum);
  //ToDo: need to remove it from here
}

export function computeVolume1(target, targetLayer, layer) {
  let ingredientVolumeSum = 0;
  let targetlayerVolume;
  let targetLayerIngredientList;

  if (layer.targetDrop < 0) {
    targetlayerVolume = targetLayer.volume;
    targetLayerIngredientList = targetLayer.wellLayerIngredientList;
  } else {
    targetlayerVolume = targetLayer.volume;
    targetLayerIngredientList = targetLayer.dropLayerIngredientList;
  }

  if (targetLayerIngredientList == undefined) {
    return;
  }

  for (let i = 0; i < targetLayerIngredientList.length; i++) {
    const targetLayerIngredient = targetLayerIngredientList[i];
    const concentration = targetLayerIngredient.preMixedConcentration;
    const stockConcentration = targetLayerIngredient.stockConcentration;

    //calculate volume for the ingredient - wellIngredient
    let volume = 0;
    // volume should be 0 if conc is 0 ( we allow stock conc to be 0 resulting volume = NaN )
    if (concentration !== 0) {
      // volume should be 0 if conc is 0 ( we allow stock conc to be 0 and it causes
      volume = Round(targetlayerVolume * (concentration / stockConcentration));
    }

    targetLayerIngredient.volume = volume;
    ingredientVolumeSum += volume;

    const targetLayerIngredientVolume = targetLayerIngredient.volume;

    if (targetLayerIngredient.isBuffer) {
      if (targetLayerIngredient.highPhIngStockId === null) {
        // Single vial buffer.
        if (
          targetLayerIngredient.lowPHVolume === null ||
          targetLayerIngredient.lowPHVolume != targetLayerIngredientVolume
        ) {
          targetLayerIngredient.lowPHVolume = targetLayerIngredientVolume;
        }

        if (targetLayerIngredient.highPHVolume !== null) {
          targetLayerIngredient.highPHVolume = null;
        }
      } else {
        // TODO .. for h2
        // Dual vial buffer. Calculate the correct Buffer Calculation for Low Fraction
        const lowVialStock = targetLayerIngredient.ingredientStock;
        const highVialStock = targetLayerIngredient.highPhIngStock;
        const ingredient = targetLayerIngredient.ingredientStockIngredient;
        const bc = new BufferCalculator(ingredient, lowVialStock, highVialStock);
        const lowFraction = bc.LowFraction(targetLayerIngredient.pH);
        const lowpHVolume = Round(lowFraction * targetLayerIngredientVolume);
        const highpHVolume = Round(targetLayerIngredientVolume - lowpHVolume);
        if (
          targetLayerIngredient.lowPHVolume == null ||
          targetLayerIngredient.lowPHVolume !== lowpHVolume
        ) {
          targetLayerIngredient.lowPHVolume = lowpHVolume;
        }
        if (
          targetLayerIngredient.highPHVolume == null ||
          targetLayerIngredient.highPHVolume !== highpHVolume
        ) {
          targetLayerIngredient.highPHVolume = highpHVolume;
        }
      }
    } else {
      // Non-buffer.
      if (targetLayerIngredient.pH !== null) {
        targetLayerIngredient.pH = null;
      }
      if (targetLayerIngredient.lowPHVolume !== null) {
        targetLayerIngredient.lowPHVolume = null;
      }
      if (targetLayerIngredient.highPHVolume !== null) {
        targetLayerIngredient.highPHVolume = null;
      }
    }
  }

  // Calculate water volumes
  if (layer.targetDrop < 0) {
    targetLayer.waterVolume = Round(layer.volume - ingredientVolumeSum);

    targetLayer.volume = layer.volume;

    target.wellWaterVolume += targetLayer.waterVolume;
  } else {
    targetLayer.waterVolume = Round(layer.volume - ingredientVolumeSum);

    targetLayer.volume = layer.volume;

    target.waterVolume += targetLayer.waterVolume;
  }
}

export function computeWellsVol(docData: ExperimentDtoClient) {
  const expWells = getExpWells(docData);
  for (const well of expWells) {
    computeWellVol(docData, well);
  }
}
function computeWellVol(docData: ExperimentDtoClient, well: WellIng) {
  let wellVolume: number = 0;
  let waterVolume: number = 0;

  //Todo- needs implementatin VolumetricWellLayers.GetWellLayers -- a long story
  //let layers = well.wellLayerList;
  const volumetricLayers: VolumetricWellLayers = VolumetricWellLayers.GetWellLayers(
    docData,
    well,
  );
  const tempLayers = volumetricLayers._layers;

  tempLayers.forEach((layer) => {
    ComputeVolumeByLayer(layer);
    wellVolume += layer.Volume;
    waterVolume += layer.WaterVolume;
  });

  // Calculate well volume
  wellVolume = Round(wellVolume);
  if (well.wellVolume == null || well.wellVolume != wellVolume) {
    well.wellVolume = wellVolume;
  }

  // Calculate water volume
  waterVolume = Round(waterVolume);
  if (well.wellWaterVolume == null || well.wellWaterVolume != waterVolume) {
    well.wellWaterVolume = waterVolume;
  }

  // Calculate final ingredient concentrations using the well volumes.
  tempLayers.forEach((layer) => {
    for (const layerIngredientRow of layer.GetIngredientRows()) {
      const layerVolume: number = layer.Volume;
      let finalConcentration: number = layerIngredientRow.preMixedConcentration;
      if (layerVolume != 0) {
        finalConcentration *= layerVolume / wellVolume;
      }
      if (
        layerIngredientRow.concentration == null ||
        layerIngredientRow.concentration != finalConcentration
      ) {
        layerIngredientRow.concentration = finalConcentration;
      }
    }
  });

  //Todo: needed
  for (const wellDrop of well.dropList) {
    ComputeVolumeByWellDrop(docData, wellDrop);
  }
}

export function setLayerError(error: string, targetLayer: TargetLayer) {
  targetLayer.hasError = true;

  if (!targetLayer.error) {
    targetLayer.error = error;
  } else {
    const existingError = targetLayer.error.trim();

    if (!existingError.includes(error.trim()) && !error.trim().includes(existingError)) {
      targetLayer.error = `${existingError} ${error}`.trim();
    }
  }
}

export function ComputeVolumeByLayer(layer: VolumetricWellLayer) {
  let ingredientVolume = 0;
  const layerVolume: number = layer.Volume;
  const ingredients = layer.GetIngredientRows();
  // Calculate ingredient volumes using the pre-mixed concentrations
  for (const layerIngredient of ingredients) {
    const concentration: number = layerIngredient.preMixedConcentration;
    const lowVolStock = getIngredientStock(layerIngredient.ingredientStockId);
    const stockConcentration: number = lowVolStock.stockConcentration;

    // Calculate volume for the ingredient - wellIngredient
    const volume: number = Round(layerVolume * (concentration / stockConcentration));

    // compute sums of volumes of ingredients - used for computing water volumes
    ingredientVolume += volume;

    if (isNullOrUndefined(layerIngredient.volume) || layerIngredient.volume != volume) {
      layerIngredient.volume = volume;
    }

    if (layerIngredient.ingredientTypeIsBuffer) {
      if (isNullOrUndefined(layerIngredient.highPhIngStockId)) {
        // Single vial buffer.
        if (
          isNullOrUndefined(layerIngredient.lowPHVolume) ||
          layerIngredient.lowPHVolume != layerIngredient.volume
        ) {
          layerIngredient.lowPHVolume = volume;
        }

        if (!isNullOrUndefined(layerIngredient.highPHVolume)) {
          layerIngredient.highPHVolume = null;
        }
      } else {
        // Dual vial buffer. Calculate the correct Buffer Calculation for Low Fraction
        //SystemDataSet.IngredientStockRow lowVialStock = layerIngredient.IngredientStockRowByFK_IngredientStock_WellLayerIngredient;
        const highVialStock = getIngredientStock(layerIngredient.highPhIngStockId);
        const ingredient = getIngredient(lowVolStock.ingredientId);
        const bc = new BufferCalculator(ingredient, lowVolStock, highVialStock);

        const lowFraction = bc.LowFraction(layerIngredient.pH);
        const lowpHVolume = Round(volume * lowFraction);
        const highpHVolume = Round(volume - lowpHVolume);

        if (
          isNullOrUndefined(layerIngredient.lowPHVolume) ||
          layerIngredient.lowPHVolume != lowpHVolume
        ) {
          layerIngredient.lowPHVolume = lowpHVolume;
        }

        if (
          isNullOrUndefined(layerIngredient.highPHVolume) ||
          layerIngredient.highPHVolume != highpHVolume
        ) {
          layerIngredient.highPHVolume = highpHVolume;
        }
      }
    } else {
      // Non-buffer.
      if (!isNullOrUndefined(layerIngredient.pH)) {
        layerIngredient.pH = null;
      }

      if (!isNullOrUndefined(layerIngredient.lowPHVolume)) {
        layerIngredient.lowPHVolume = null;
      }

      if (!isNullOrUndefined(layerIngredient.highPHVolume)) {
        layerIngredient.highPHVolume = null;
      }
    }
  }

  // Calculate water volume
  const waterVolume: number = Round(layerVolume - ingredientVolume);
  if (isNullOrUndefined(layer.WaterVolume) || layer.WaterVolume != waterVolume) {
    layer.WaterVolume = waterVolume;
  }
}

export function ComputeVolumeByWellDrop(docData, wellDrop: Drop) {
  let wellDropVolume = 0;
  let waterVolume = 0;

  const layers = VolumetricWellDropLayers.GetWellDropLayers(docData, wellDrop);

  for (const [, layer] of layers.GetEnumerator()) {
    if (layer.IsProteinLayer || layer.IsSeedLayer || layer.IsFragmentLayer) {
      // Protein layers shouldn't get calculated.
      // There is no water nor ingredients to calculate volumes for in the protein layer.
      // Add the volume (drop well + drop protein) to the total volume.
      wellDropVolume += layer.Volume;
    } else {
      ComputeVolWellDrop(layer);
      wellDropVolume += layer.Volume;
      waterVolume += layer.WaterVolume;
    }
  }

  // Calculate well drop volume
  wellDropVolume = Number(wellDropVolume.toFixed(3));
  if (isNullOrUndefined(wellDrop.volume) || wellDrop.volume != wellDropVolume) {
    wellDrop.volume = wellDropVolume;
  }

  // Calculate water volume
  waterVolume = Round(waterVolume);
  if (isNullOrUndefined(wellDrop.waterVolume) || wellDrop.waterVolume != waterVolume) {
    wellDrop.waterVolume = waterVolume;
  }

  // Calculate final ingredient concentrations using the well drop volumes.
  for (const [, layer] of layers.GetEnumerator()) {
    for (const layerIngredientRow of layer.GetIngredientRows()) {
      const layerVolume = layer.Volume;
      let finalConcentration = layerIngredientRow.preMixedConcentration;
      if (layerVolume != 0) {
        finalConcentration *= layerVolume / wellDropVolume;
      }
      finalConcentration = Round(finalConcentration);
      if (
        isNullOrUndefined(layerIngredientRow.concentration) ||
        layerIngredientRow.concentration != finalConcentration
      ) {
        layerIngredientRow.concentration = finalConcentration;
      }
    }
  }
}

export function ComputeVolWellDrop(layer: VolumetricWellDropLayer) {
  if (layer.IsProteinLayer()) {
    // Currently protein layers shouldn't get calculated.
    // This has to do with the protein layer modifies the WellDropRow, instead of the WellDropLayerRow.
    return;
  }

  let ingredientVolume = 0;
  const layerVolume = layer.Volume;
  const ingredients = layer.wellDropLayerIngredientRows;

  // Calculate ingredient volumes using the pre-mixed concentrations
  for (const layerIngredient of ingredients) {
    const concentration = layerIngredient.preMixedConcentration;
    const lowVolStock = getIngredientStock(layerIngredient.ingredientStockId);
    const stockConcentration: number = lowVolStock.stockConcentration;
    // Calculate volume for the ingredient - wellDropIngredient
    const volume = Round(layerVolume * (concentration / stockConcentration));

    // compute sums of volumes of ingredients - used for computing water volumes
    ingredientVolume += volume;

    if (isNullOrUndefined(layerIngredient.volume) || layerIngredient.volume != volume) {
      layerIngredient.volume = volume;
    }

    if (isBufferIngredient(layerIngredient.ingredientIngredientTypeId)) {
      if (isNullOrUndefined(layerIngredient.highPhIngStockId)) {
        // Single vial buffer.
        if (
          isNullOrUndefined(layerIngredient.lowPHVolume) ||
          layerIngredient.lowPHVolume != layerIngredient.volume
        ) {
          layerIngredient.lowPHVolume = volume;
        }

        if (!isNullOrUndefined(layerIngredient.highPHVolume)) {
          layerIngredient.highPHVolume = null;
        }
      } else {
        const lowVialStock = getIngredientStock(layerIngredient.ingredientStockId);
        const highVialStock = getIngredientStock(layerIngredient.highPhIngStockId);
        const ingredient = getIngredient(lowVolStock.ingredientId);
        const bc = new BufferCalculator(ingredient, lowVialStock, highVialStock);

        const lowFraction = bc.LowFraction(layerIngredient.pH);
        const lowpHVolume = Round(volume * lowFraction);
        const highpHVolume = Round(volume - lowpHVolume);

        if (
          isNullOrUndefined(layerIngredient.lowPHVolume) ||
          layerIngredient.lowPHVolume != lowpHVolume
        ) {
          layerIngredient.lowPHVolume = lowpHVolume;
        }

        if (
          isNullOrUndefined(layerIngredient.highPHVolume) ||
          layerIngredient.highPHVolume != highpHVolume
        ) {
          layerIngredient.highPHVolume = highpHVolume;
        }
      }
    } else {
      // Non-buffer.
      if (!isNullOrUndefined(layerIngredient.pH)) {
        layerIngredient.pH = null;
      }

      if (!isNullOrUndefined(layerIngredient.lowPHVolume)) {
        layerIngredient.lowPHVolume = null;
      }

      if (!isNullOrUndefined(layerIngredient.highPHVolume)) {
        layerIngredient.highPHVolume = null;
      }
    }
  }

  // Calculate water volume
  const waterVolume = Round(layerVolume - ingredientVolume);
  if (isNullOrUndefined(layer.WaterVolume) || layer.WaterVolume != waterVolume) {
    layer.WaterVolume = waterVolume;
  }
}
